import { ICorrectPaymentDto, IPaymentCorrectionContext } from '../../compiler/billing.interface';
/**
 * Billing Management API Service
 * Base URL: /procurement/api/billing
 *
 * ใช้ multiFranchiseAxios เพื่อส่ง X-Franchise-Context header
 * Reference: BILLING_MANAGEMENT_API_GUIDE.md
 */

import { multiFranchiseAxios } from "../axios/multiFranchiseAxios";
import {
  IBillingReportParams,
  IBillingPeriodsQueryParams,
  IBillingPeriodsResponse,
  IBillingRoundsQueryParams,
  IBillingRoundsResponse,
  IBillingCustomersQueryParams,
  IBillingCustomersResponse,
  IBillingInvoicesQueryParams,
  IBillingInvoicesResponse,
  ICreatePaymentDto,
  ICancelBillingPaymentDto,
  ICreatePaymentResponse,
  IUpdatePaymentDto,
  IUpdatePaymentResponse,
  IDeletePaymentResponse,
  IBillingPaymentsQueryParams,
  IBillingPaymentsResponse,
  IBillingPaymentDetailResponse,
  IApproveBillingPaymentResponse,
  IRejectBillingPaymentDto,
  IRejectBillingPaymentResponse,
  IUpdatePaymentEvidenceVerificationDto,
  IUpdatePaymentEvidenceVerificationResponse,
  IUploadEvidenceResponse,
  IUploadPaymentEvidenceOptions,
  ICreateBillingRoundDto,
  IUpdateBillingRoundDto,
  IRegenerateBillingRoundsDto,
  IClearRoundsResponse,
  IRegenerateRoundsResponse,
  IPeriodRoundsResponse,
  IBillingCustomersSummaryQueryParams,
  IBillingCustomersSummaryResponse,
} from "../../compiler/billing.interface";

const BASE_PATH = "/procurement/api/billing";

const appendCustomerScopeFlag = (queryParams: URLSearchParams, enforceCustomerScope?: boolean) => {
  if (enforceCustomerScope) {
    queryParams.append("enforce_customer_scope", "true");
  }
};

// ============================================================
// 0. Billing Periods (server-side grouped)
// ============================================================

/**
 * ดึงรายการรอบบิลแบบ group by ช่วงวันที่ (server-side)
 * GET /procurement/api/billing/periods
 */
export const APIGetBillingPeriods = async (
  params?: IBillingPeriodsQueryParams
): Promise<IBillingPeriodsResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.year) queryParams.append("year", String(params.year));
  if (params?.month !== undefined && params.month > 0)
    queryParams.append("month", String(params.month));
  if (params?.status && params.status !== "all") queryParams.append("status", params.status);
  appendCustomerScopeFlag(queryParams, params?.enforce_customer_scope);
  const qs = queryParams.toString();
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/periods${qs ? `?${qs}` : ""}`);
  return response.data;
};

/**
 * ดึงรายละเอียดรอบบิลรายลูกค้าในช่วงวันที่ (lazy load เมื่อ expand row)
 * GET /procurement/api/billing/periods/rounds?start_date=&end_date=
 */
export const APIGetPeriodRounds = async (
  startDate: string,
  endDate: string
): Promise<IPeriodRoundsResponse> => {
  const response = await multiFranchiseAxios.get(
    `${BASE_PATH}/periods/rounds?start_date=${startDate}&end_date=${endDate}`
  );
  return response.data;
};

/**
 * สรุปยอดค้างชำระรายลูกค้าข้ามรอบบิล (cross-period)
 * GET /procurement/api/billing/customers-summary
 */
export const APIGetCustomersBillingSummary = async (
  params?: IBillingCustomersSummaryQueryParams
): Promise<IBillingCustomersSummaryResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.year) queryParams.append("year", params.year);
  if (params?.month !== undefined && params.month > 0)
    queryParams.append("month", String(params.month));
  if (params?.search?.trim()) queryParams.append("search", params.search.trim());
  if (params?.status_filter && params.status_filter !== "all")
    queryParams.append("status_filter", params.status_filter);
  appendCustomerScopeFlag(queryParams, params?.enforce_customer_scope);
  const qs = queryParams.toString();
  const response = await multiFranchiseAxios.get(
    `${BASE_PATH}/customers-summary${qs ? `?${qs}` : ""}`
  );
  return response.data;
};

// ============================================================
// 1. Billing Rounds
// GET /procurement/api/billing/rounds
export const APIGetBillingRounds = async (
  params?: IBillingRoundsQueryParams
): Promise<IBillingRoundsResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.page) queryParams.append("page", String(params.page));
  if (params?.limit) queryParams.append("limit", String(params.limit));
  if (params?.status) queryParams.append("status", params.status);
  if (params?.year) queryParams.append("year", String(params.year));
  if (params?.search && params.search.trim()) queryParams.append("search", params.search.trim());
  appendCustomerScopeFlag(queryParams, params?.enforce_customer_scope);

  const queryString = queryParams.toString();
  const url = `${BASE_PATH}/rounds${queryString ? `?${queryString}` : ""}`;

  const response = await multiFranchiseAxios.get(url);
  return response.data;
};

// ============================================================
// 2. Customer Billing Summary
// ============================================================

/**
 * ดึงรายการลูกค้าในรอบวางบิลที่เลือก พร้อมสรุปยอด Invoice
 * GET /procurement/api/billing/customers
 */
export const APIGetBillingCustomers = async (
  params: IBillingCustomersQueryParams
): Promise<IBillingCustomersResponse> => {
  const queryParams = new URLSearchParams();

  queryParams.append("start_date", params.start_date);
  queryParams.append("end_date", params.end_date);

  if (params.customer_code) queryParams.append("customer_code", params.customer_code);
  if (params.search && params.search.trim()) queryParams.append("search", params.search.trim());
  if (params.status) queryParams.append("status", params.status);
  if (params.page) queryParams.append("page", String(params.page));
  if (params.limit) queryParams.append("limit", String(params.limit));
  appendCustomerScopeFlag(queryParams, params.enforce_customer_scope);

  const response = await multiFranchiseAxios.get(
    `${BASE_PATH}/customers?${queryParams.toString()}`
  );
  return response.data;
};

// ============================================================
// 2.5 Customer Invoices Detail
// ============================================================

/**
 * ดึงรายการ Invoice ของลูกค้ารายนั้นในรอบวางบิล
 * GET /procurement/api/billing/invoices?customer_code=X&start_date=Y&end_date=Z
 */
export const APIGetBillingCustomerInvoices = async (
  params: IBillingInvoicesQueryParams
): Promise<IBillingInvoicesResponse> => {
  const queryParams = new URLSearchParams();

  queryParams.append("customer_code", params.customer_code);
  queryParams.append("start_date", params.start_date);
  queryParams.append("end_date", params.end_date);

  if (params.search && params.search.trim()) queryParams.append("search", params.search.trim());
  if (params.payment_status) queryParams.append("payment_status", params.payment_status);
  if (params.page) queryParams.append("page", String(params.page));
  if (params.limit) queryParams.append("limit", String(params.limit));
  appendCustomerScopeFlag(queryParams, params.enforce_customer_scope);

  const response = await multiFranchiseAxios.get(`${BASE_PATH}/invoices?${queryParams.toString()}`);
  return response.data;
};

/** Complete server selection; no page-sized fallback when API is older. */
export const APIGetBillingPaymentSelection = async (params: IBillingInvoicesQueryParams): Promise<IBillingInvoicesResponse & { data: IBillingInvoicesResponse["data"] & { selection_version: number } }> => {
  const query = new URLSearchParams({ customer_code: params.customer_code, start_date: params.start_date, end_date: params.end_date });
  if (params.search?.trim()) query.set("search", params.search.trim());
  if (params.payment_status) query.set("payment_status", params.payment_status);
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/invoices/payment-selection?${query}`);
  return response.data;
};

// ============================================================
// 3. Payment CRUD
// ============================================================

/**
 * สร้างรายการชำระเงินใหม่ (รองรับ multipart/form-data สำหรับอัปโหลดหลักฐาน)
 * POST /procurement/api/billing/payments
 */
export const APICreateBillingPayment = async (
  dto: ICreatePaymentDto
): Promise<ICreatePaymentResponse> => {
  const formData = new FormData();
  const queryParams = new URLSearchParams();

  if (dto.client_request_id) formData.append("client_request_id", dto.client_request_id);
  formData.append("invoice_uuids", JSON.stringify(dto.invoice_uuids));
  formData.append("customer_code", dto.customer_code);
  formData.append("total_paid_amount", String(dto.total_paid_amount));
  formData.append("payment_method", dto.payment_method);
  formData.append("payment_date", dto.payment_date);

  if (dto.billing_round) formData.append("billing_round", dto.billing_round);
  if (dto.billing_round_uuid) formData.append("billing_round_uuid", dto.billing_round_uuid);
  if (dto.payment_reference) formData.append("payment_reference", dto.payment_reference);
  if (dto.bank_name) formData.append("bank_name", dto.bank_name);
  if (dto.bank_account) formData.append("bank_account", dto.bank_account);
  if (dto.notes) formData.append("notes", dto.notes);
  if (dto.evidence_source) formData.append("evidence_source", dto.evidence_source);
  if (dto.enforce_customer_scope) {
    formData.append("enforce_customer_scope", "true");
    queryParams.append("enforce_customer_scope", "true");
  }
  if (dto.expected_invoice_balances) formData.append("expected_invoice_balances", JSON.stringify(dto.expected_invoice_balances));
  if (dto.allocation) formData.append("allocation", JSON.stringify(dto.allocation));
  if (dto.evidence_metadata)
    formData.append("evidence_metadata", JSON.stringify(dto.evidence_metadata));

  if (dto.files?.length) {
    dto.files.forEach((file) => formData.append("files", file));
  }

  const qs = queryParams.toString();
  const response = await multiFranchiseAxios.post(
    `${BASE_PATH}/payments${qs ? `?${qs}` : ""}`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000, // 60s for file upload
    }
  );
  return response.data;
};

/**
 * ดึงรายการชำระเงิน
 * GET /procurement/api/billing/payments
 */
export const APIGetBillingPayments = async (
  params?: IBillingPaymentsQueryParams
): Promise<IBillingPaymentsResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.set("status", params.status);
  if (params?.client_request_id) queryParams.set("client_request_id", params.client_request_id);

  if (params?.billing_round_uuid)
    queryParams.append("billing_round_uuid", params.billing_round_uuid);
  if (params?.customer_code) queryParams.append("customer_code", params.customer_code);
  if (params?.billing_round) queryParams.append("billing_round", params.billing_round);
  if (params?.invoice_uuid) queryParams.append("invoice_uuid", params.invoice_uuid);
  if (params?.start_date) queryParams.append("start_date", params.start_date);
  if (params?.end_date) queryParams.append("end_date", params.end_date);
  if (params?.page) queryParams.append("page", String(params.page));
  if (params?.limit) queryParams.append("limit", String(params.limit));
  appendCustomerScopeFlag(queryParams, params?.enforce_customer_scope);

  const queryString = queryParams.toString();
  const url = `${BASE_PATH}/payments${queryString ? `?${queryString}` : ""}`;

  const response = await multiFranchiseAxios.get(url);
  return response.data;
};

/**
 * ดึงรายละเอียดการชำระเงิน
 * GET /procurement/api/billing/payments/:uuid
 */
export const APIGetBillingPaymentDetail = async (
  uuid: string
): Promise<IBillingPaymentDetailResponse> => {
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/payments/${uuid}`);
  return response.data;
};

/**
 * อนุมัติหลักฐานการชำระเงินและ post ยอดเข้า invoices
 * POST /procurement/api/billing/payments/:uuid/approve
 */
export const APIApproveBillingPayment = async (
  uuid: string
): Promise<IApproveBillingPaymentResponse> => {
  const response = await multiFranchiseAxios.post(`${BASE_PATH}/payments/${uuid}/approve`);
  return response.data;
};

/**
 * ไม่อนุมัติหลักฐานการชำระเงิน
 * POST /procurement/api/billing/payments/:uuid/reject
 */
export const APIRejectBillingPayment = async (
  uuid: string,
  dto: IRejectBillingPaymentDto
): Promise<IRejectBillingPaymentResponse> => {
  const response = await multiFranchiseAxios.post(`${BASE_PATH}/payments/${uuid}/reject`, dto);
  return response.data;
};

/**
 * แก้ไขรายการชำระเงิน
 * PUT /procurement/api/billing/payments/:uuid
 */
export const APIUpdateBillingPayment = async (
  uuid: string,
  dto: IUpdatePaymentDto
): Promise<IUpdatePaymentResponse> => {
  const formData = new FormData();

  if (dto.invoice_uuids) formData.append("invoice_uuids", JSON.stringify(dto.invoice_uuids));
  if (dto.total_paid_amount !== undefined)
    formData.append("total_paid_amount", String(dto.total_paid_amount));
  if (dto.payment_method) formData.append("payment_method", dto.payment_method);
  if (dto.payment_date) formData.append("payment_date", dto.payment_date);
  if (dto.payment_reference) formData.append("payment_reference", dto.payment_reference);
  if (dto.bank_name) formData.append("bank_name", dto.bank_name);
  if (dto.bank_account) formData.append("bank_account", dto.bank_account);
  if (dto.notes !== undefined) formData.append("notes", dto.notes || "");
  if (dto.allocation) formData.append("allocation", JSON.stringify(dto.allocation));
  if (dto.remove_file_uuids?.length)
    formData.append("remove_file_uuids", JSON.stringify(dto.remove_file_uuids));

  if (dto.files?.length) {
    dto.files.forEach((file) => formData.append("files", file));
  }

  const response = await multiFranchiseAxios.put(`${BASE_PATH}/payments/${uuid}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return response.data;
};

/**
 * บันทึกผล OCR/manual verification ของไฟล์หลักฐาน
 * PATCH /procurement/api/billing/payments/evidence/:uuid/verification
 */
export const APIUpdatePaymentEvidenceVerification = async (
  evidenceUuid: string,
  dto: IUpdatePaymentEvidenceVerificationDto
): Promise<IUpdatePaymentEvidenceVerificationResponse> => {
  const response = await multiFranchiseAxios.patch(
    `${BASE_PATH}/payments/evidence/${evidenceUuid}/verification`,
    dto
  );
  return response.data;
};

/**
 * ลบรายการชำระเงิน (soft delete)
 * DELETE /procurement/api/billing/payments/:uuid
 */
export const APIDeleteBillingPayment = async (
  uuid: string,
  dto?: ICancelBillingPaymentDto
): Promise<IDeletePaymentResponse> => {
  const response = await multiFranchiseAxios.delete(`${BASE_PATH}/payments/${uuid}`, {
    data: dto,
  });
  return response.data;
};

/**
 * อัปโหลดหลักฐานเพิ่มเติม
 * POST /procurement/api/billing/payments/evidence
 */
// ============================================================
// Billing Round Management
// ============================================================

/**
 * สร้างรอบบิล Manual
 * POST /procurement/api/billing/rounds
 */
export const APICreateBillingRound = async (
  dto: ICreateBillingRoundDto
): Promise<{ uuid: string; round_label: string }> => {
  const response = await multiFranchiseAxios.post(`${BASE_PATH}/rounds`, dto);
  return response.data;
};

/**
 * แก้ไขรอบบิล
 * PUT /procurement/api/billing/rounds/:uuid
 */
export const APIUpdateBillingRound = async (
  uuid: string,
  dto: IUpdateBillingRoundDto
): Promise<{ uuid: string; round_label: string }> => {
  const response = await multiFranchiseAxios.put(`${BASE_PATH}/rounds/${uuid}`, dto);
  return response.data;
};

/**
 * ลบรอบบิล (Soft Delete)
 * DELETE /procurement/api/billing/rounds/:uuid
 */
export const APIDeleteBillingRound = async (uuid: string): Promise<{ message: string }> => {
  const response = await multiFranchiseAxios.delete(`${BASE_PATH}/rounds/${uuid}`);
  return response.data;
};

/**
 * เคลียร์รอบบิลที่ไม่มีการชำระทั้งหมด
 * POST /procurement/api/billing/rounds/clear-all
 */
export const APIClearUnpaidBillingRounds = async (): Promise<IClearRoundsResponse> => {
  const response = await multiFranchiseAxios.post(`${BASE_PATH}/rounds/clear-all`);
  return response.data;
};

/**
 * Regenerate รอบบิลจาก billing cycle templates
 * POST /procurement/api/billing/rounds/regenerate
 */
export const APIRegenerateBillingRounds = async (
  dto: IRegenerateBillingRoundsDto
): Promise<IRegenerateRoundsResponse> => {
  const response = await multiFranchiseAxios.post(`${BASE_PATH}/rounds/regenerate`, dto);
  return response.data;
};

export const APIUploadPaymentEvidence = async (
  paymentUuid: string,
  files: File[],
  options?: IUploadPaymentEvidenceOptions
): Promise<IUploadEvidenceResponse> => {
  const formData = new FormData();
  const queryParams = new URLSearchParams();
  formData.append("payment_uuid", paymentUuid);
  if (options?.evidence_source) formData.append("evidence_source", options.evidence_source);
  if (options?.enforce_customer_scope) {
    formData.append("enforce_customer_scope", "true");
    queryParams.append("enforce_customer_scope", "true");
  }
  files.forEach((file) => formData.append("files", file));

  const qs = queryParams.toString();
  const response = await multiFranchiseAxios.post(
    `${BASE_PATH}/payments/evidence${qs ? `?${qs}` : ""}`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    }
  );
  return response.data;
};

export const APIEnsureBillingRounds = async (year: number) => {
  const response = await multiFranchiseAxios.post(`${BASE_PATH}/rounds/ensure`, { year });
  return response.data;
};
export const APIExportBillingReport = async (params: IBillingReportParams): Promise<Blob> => {
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/reports/export`, { params, responseType: "blob", timeout: 120000 });
  return response.data;
};


export const APIGetPaymentCorrectionContext = async (uuid: string): Promise<{ status: boolean; data: IPaymentCorrectionContext }> =>
  (await multiFranchiseAxios.get(`${BASE_PATH}/payments/${uuid}/correction-context`)).data;

export const APICorrectBillingPayment = async (uuid: string, dto: ICorrectPaymentDto): Promise<{ status: boolean; message: string; data: { payment_uuid: string; payment_id: string; replayed: boolean } }> => {
  const data = new FormData();
  const { files, ...fields } = dto;
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined) data.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
  });
  (files || []).forEach(file => data.append('files', file));
  return (await multiFranchiseAxios.post(`${BASE_PATH}/payments/${uuid}/correct`, data, { timeout: 60000, headers: { 'Content-Type': 'multipart/form-data' } })).data;
};
